import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone, Store, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import { getCMSContent } from '@/lib/api';

const DAYS = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'];
const DAY_LABELS = { lundi:'Lundi', mardi:'Mardi', mercredi:'Mercredi', jeudi:'Jeudi', vendredi:'Vendredi', samedi:'Samedi', dimanche:'Dimanche' };

const NAPCard = ({ phone, cms }) => {
  const hasSchedule = DAYS.some(d => cms[`hours_${d}`]);
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
          <Store className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Boutique</p>
              <p className="font-heading font-semibold text-foreground">Villa Marceau</p>
              <p className="text-sm text-muted-foreground">Concept Store</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Adresse</p>
              <p className="text-sm font-medium text-foreground">26 rue Marceau</p>
              <p className="text-sm text-muted-foreground">37000 Tours</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Téléphone</p>
              <p className="text-sm font-medium text-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {phone ? <a href={`tel:${phone}`} className="hover:text-primary transition-colors">{phone}</a> : <span className="italic text-muted-foreground">À renseigner</span>}
              </p>
            </div>
          </div>

          {hasSchedule && (
            <div className="border-t border-stone-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-primary shrink-0" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Horaires d'ouverture</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DAYS.map(day => {
                  const hours = cms[`hours_${day}`];
                  if (!hours) return null;
                  const isClosed = hours.toLowerCase() === 'fermé' || hours.toLowerCase() === 'ferme';
                  return (
                    <div key={day} className="flex justify-between items-center py-1 px-2 rounded bg-white border border-stone-100 text-xs">
                      <span className="font-medium text-stone-600">{DAY_LABELS[day].slice(0,3)}.</span>
                      <span className={isClosed ? 'text-red-500' : 'text-stone-700'}>{hours}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const textParagraphs = [
  {
    text: "Au cœur de Tours, une porte s'ouvre sur un ailleurs…",
    style: 'intro',
  },
  {
    text: "C'est au 26 rue Marceau que l'histoire continue de s'écrire, dans un lieu pensé comme une parenthèse hors du temps. Derrière la vitrine de Villa Marceau, les couleurs dansent, les matières réconfortent et les parfums invitent doucement au voyage.",
    style: 'normal',
  },
  {
    text: "C'est ici que je vous accueille, avec joie et simplicité, comme on reçoit chez soi. Chaque visite est une rencontre, un moment à part, une invitation à ralentir et à découvrir mon univers.",
    style: 'normal',
  },
  {
    text: "Que vous soyez de passage ou fidèle au quartier, je serai enchantée de vous ouvrir les portes de la boutique, de partager avec vous mes inspirations et de vous accompagner dans vos coups de cœur.",
    style: 'normal',
  },
  {
    text: "Poussez la porte… et laissez-vous porter.",
    style: 'quote',
  },
  {
    text: "À très vite chez Villa Marceau.",
    style: 'signature',
  },
];

export default function OuNousTrouverPage() {
  const [cms, setCms] = useState({});

  useEffect(() => {
    getCMSContent().then(r => {
      if (r.data) setCms(r.data);
    }).catch(() => {});
  }, []);
  return (
    <PublicLayout>
      <SEO
        title="Où Nous Trouver — 26 rue Marceau, 37000 Tours"
        description="Retrouvez Villa Marceau au 26 rue Marceau, 37000 Tours. Horaires d'ouverture, plan d'accès et contact de votre concept store bohème."
        canonical="/ou-nous-trouver"
      />
      {/* Page Header */}
      <section className="pt-12 pb-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4 leading-tight">
              Où nous trouver
            </h1>
            <div className="w-16 h-0.5 bg-primary rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Boutique Photo — full, uncropped */}
        <div className="px-6 lg:px-12 pb-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl shadow-lg bg-stone-50 mx-auto" style={{ maxWidth: '75%' }}>
              <img
                src="/images/boutique.jpg"
                alt="Façade de Villa Marceau, 26 rue Marceau, Tours"
                className="w-full h-auto"
                loading="eager"
              />
            </div>
            {/* Address overlay badge */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2 shadow-md">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">26 rue Marceau, 37000 Tours</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Text Content */}
      <section className="py-4 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="space-y-5"
          >
            {textParagraphs.map((para, idx) => (
              <p
                key={idx}
                className={`leading-relaxed ${
                  para.style === 'intro'
                    ? 'font-heading text-2xl font-semibold text-foreground'
                    : para.style === 'quote'
                    ? 'font-heading text-xl italic text-primary border-l-4 border-primary pl-5 py-1'
                    : para.style === 'signature'
                    ? 'font-heading text-xl font-semibold text-foreground'
                    : 'text-base text-foreground/80'
                }`}
              >
                {para.text}
              </p>
            ))}

            {/* Address highlight */}
            <div className="mt-6 pt-4 border-t border-border/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-foreground">26 rue Marceau, 37000 Tours</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-10 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden rounded-2xl shadow-md border border-border/50"
          >
            <iframe
              title="Villa Marceau sur Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2699.745870019879!2d0.6888!3d47.3941!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47fcd5c7e1ca3d15%3A0xbec8a68c01d2be4!2s26%20Rue%20Marceau%2C%2037000%20Tours!5e0!3m2!1sfr!2sfr!4v1"
              width="100%"
              height="380"
              style={{ border: 0, display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* NAP + Links */}
      <section className="py-12 px-6 lg:px-12 bg-stone-50/60">
        <div className="max-w-7xl mx-auto space-y-6">
          <NAPCard phone={cms.phone_number} cms={cms} />

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/commander-en-ligne"
              className="inline-flex items-center justify-center gap-2 bg-foreground text-white px-6 py-3 rounded-full font-medium hover:bg-foreground/90 transition-colors"
              data-testid="ou-trouver-shop-link"
            >
              Commander en ligne
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/notre-histoire"
              className="inline-flex items-center justify-center gap-2 border border-border px-6 py-3 rounded-full font-medium text-foreground hover:bg-accent transition-colors"
              data-testid="ou-trouver-histoire-link"
            >
              Notre histoire
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground px-6 py-3 transition-colors text-sm"
            >
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
